"use client";

import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  motion,
  useMotionValue,
  useTransform,
  useSpring,
  useReducedMotion,
  AnimatePresence
} from "motion/react";
import { Chip, Button, Card } from "@heroui/react";
import { Projects } from "@/components/utils/data";

// Type definitions for Project screenshots
interface Screenshot {
  image: string;
  title: string;
  type: string;
}

export default function CaseStudies() {
  const [activeTab, setActiveTab] = useState<string>("0");
  const [isDark, setIsDark] = useState(false);
  const reducedMotion = useReducedMotion();

  // Sync theme changes
  useEffect(() => {
    const checkDark = () => {
      setIsDark(document.documentElement.classList.contains("dark"));
    };
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  // Use the first 3 projects for the Home section
  const featuredProjects = Projects.slice(0, 3);
  const activeTabIdx = parseInt(activeTab, 10);
  const activeProject = featuredProjects[activeTabIdx] || featuredProjects[0];

  // Auto change active project every 6 seconds.
  // Including activeTab in dependencies resets the interval on manual click/tab change.
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTab((prev) => {
        const nextIdx = (parseInt(prev, 10) + 1) % featuredProjects.length;
        return nextIdx.toString();
      });
    }, 6000);
    return () => clearInterval(timer);
  }, [activeTab, featuredProjects.length]);

  // Dynamic branding configurations for each project
  const brandThemes = [
    {
      accent: "text-amber-500 dark:text-amber-400",
      accentBg: "bg-amber-500/10 dark:bg-amber-400/10",
      border: "border-amber-500/20 dark:border-amber-500/10",
      glow: "from-amber-500/15 via-amber-600/5 to-transparent",
      chip: "bg-amber-50 dark:bg-amber-950/20 text-amber-600 dark:text-amber-300 border-amber-250/30 dark:border-amber-800/40",
      button: "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-amber-500/20 text-white",
      iconColor: "#f59e0b",
      tabGradient: "bg-gradient-to-r from-amber-500/12 to-orange-500/12 dark:from-amber-400/10 dark:to-orange-400/10 border-amber-500/25 dark:border-amber-400/30",
      tabTextActive: "text-amber-600 dark:text-amber-400 font-extrabold",
    },
    {
      accent: "text-indigo-500 dark:text-indigo-400",
      accentBg: "bg-indigo-500/10 dark:bg-indigo-400/10",
      border: "border-indigo-500/20 dark:border-indigo-500/10",
      glow: "from-indigo-500/15 via-indigo-600/5 to-transparent",
      chip: "bg-indigo-50 dark:bg-indigo-950/20 text-indigo-600 dark:text-indigo-300 border-indigo-250/30 dark:border-indigo-800/40",
      button: "bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 shadow-indigo-500/20 text-white",
      iconColor: "#6366f1",
      tabGradient: "bg-gradient-to-r from-indigo-500/12 to-blue-500/12 dark:from-indigo-400/10 dark:to-blue-400/10 border-indigo-500/25 dark:border-indigo-400/30",
      tabTextActive: "text-indigo-600 dark:text-indigo-400 font-extrabold",
    },
    {
      accent: "text-purple-500 dark:text-purple-400",
      accentBg: "bg-purple-500/10 dark:bg-purple-400/10",
      border: "border-purple-500/20 dark:border-purple-500/10",
      glow: "from-purple-500/15 via-purple-600/5 to-transparent",
      chip: "bg-purple-50 dark:bg-purple-950/20 text-purple-600 dark:text-purple-300 border-purple-250/30 dark:border-purple-800/40",
      button: "bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 shadow-purple-500/20 text-white",
      iconColor: "#a855f7",
      tabGradient: "bg-gradient-to-r from-purple-500/12 to-pink-500/12 dark:from-purple-400/10 dark:to-pink-400/10 border-purple-500/25 dark:border-purple-400/30",
      tabTextActive: "text-purple-600 dark:text-purple-400 font-extrabold",
    },
  ];

  const theme = brandThemes[activeTabIdx] || brandThemes[0];

  // Resolve current theme screenshots
  const getScreenshots = (project: typeof Projects[0]) => {
    const desiredType = isDark ? "dark" : "light";
    const desktop = project.desktopScreenshots?.find((s: Screenshot) => s.type === desiredType) || project.desktopScreenshots?.[0];
    const mobile = project.mobileScreenshots?.find((s: Screenshot) => s.type === desiredType) || project.mobileScreenshots?.[0];
    
    const resolveUrl = (val: any) => {
      if (!val) return "";
      return typeof val === "string" ? val : val.src;
    };

    return {
      desktopUrl: resolveUrl(desktop?.image || (isDark ? project.imgDark : project.img) || project.img),
      mobileUrl: resolveUrl(mobile?.image || (isDark ? project.imgDark : project.img) || project.img),
    };
  };

  const currentScreens = getScreenshots(activeProject);

  // Mouse 3D tilt tracking for premium mockup depth
  const containerRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // Smooth spring constants for realistic inertial movement
  const springX = useSpring(x, { stiffness: 120, damping: 25 });
  const springY = useSpring(y, { stiffness: 120, damping: 25 });

  // Map mouse coordinates to 3D rotation transforms
  const rotateX = useTransform(springY, [-0.5, 0.5], [8, -8]);
  const rotateY = useTransform(springX, [-0.5, 0.5], [-8, 8]);

  // Subtle offset/parallax mapping for the layered mobile screen
  const mobileTranslateX = useTransform(springX, [-0.5, 0.5], [-12, 12]);
  const mobileTranslateY = useTransform(springY, [-0.5, 0.5], [-12, 12]);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current || reducedMotion) return;
    const rect = containerRef.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseXVal = e.clientX - rect.left;
    const mouseYVal = e.clientY - rect.top;

    x.set(mouseXVal / width - 0.5);
    y.set(mouseYVal / height - 0.5);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <section className="relative w-full py-24 sm:py-32 px-4 overflow-hidden bg-neutral-50 dark:bg-black transition-colors duration-500">
      
      {/* Background Interactive Mesh & Grid Pattern */}
      <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.08, 0.95, 1],
            opacity: [0.3, 0.45, 0.3],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className={`absolute top-[15%] left-[20%] h-[500px] w-[500px] rounded-full blur-[130px] bg-gradient-to-tr ${theme.glow}`}
        />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808006_1px,transparent_1px),linear-gradient(to_bottom,#80808006_1px,transparent_1px)] bg-[size:32px_32px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_85%,transparent_100%)]" />
      </div>

      <div className="w-full max-w-7xl mx-auto flex flex-col gap-12 sm:gap-16">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="mb-4"
          >
            <Chip
              size="sm"
              variant="soft"
              className="bg-neutral-200/40 dark:bg-neutral-900/40 text-[10px] uppercase tracking-widest font-extrabold text-neutral-500 dark:text-neutral-400 px-4 py-1.5 border border-neutral-300/20 dark:border-neutral-800/30 rounded-full font-outfit"
            >
              Case Studies
            </Chip>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight font-recoleta text-neutral-900 dark:text-white mb-5"
          >
            Selected{" "}
            <span className="bg-gradient-to-r from-sky-500 via-blue-500 to-indigo-500 bg-clip-text text-transparent">
              Creative Works
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.15 }}
            className="text-neutral-500 dark:text-neutral-400 text-base sm:text-lg leading-relaxed font-outfit max-w-2xl"
          >
            Explore our latest enterprise-grade web applications, custom platforms, and digital solutions designed to elevate brands and scale performance.
          </motion.p>
        </div>

        {/* Custom Interactive Selector (Tabs) */}
        <div className="flex justify-center w-full px-2 py-10">
          <div 
            role="tablist" 
            aria-label="Case Studies Projects" 
            className="w-full max-w-3xl justify-between bg-neutral-100/40 dark:bg-neutral-900/30 backdrop-blur-2xl border border-neutral-200/40 dark:border-neutral-800/30 rounded-full p-2 flex gap-2 sm:gap-4"
          >
            {featuredProjects.map((project, index) => {
              const isActive = index === activeTabIdx;
              return (
                <button
                  key={index.toString()}
                  id={`project-tab-${index}`}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`project-tabpanel-${index}`}
                  onClick={() => setActiveTab(index.toString())}
                  className={`relative flex-1 py-5 text-center transition-all duration-300 z-10 cursor-pointer select-none rounded-full outline-none border-none bg-transparent ${
                    isActive ? theme.tabTextActive + " scale-[1.02]" : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200"
                  }`}
                >
                  <div className="flex items-center justify-center gap-2 py-1.5 px-3">
                    <span className={`text-[10px] font-mono tracking-wider font-semibold transition-colors duration-300 ${isActive ? theme.accent : "text-neutral-400 dark:text-neutral-600"}`}>
                      0{index + 1}.
                    </span>
                    <span className="text-xs sm:text-sm font-bold tracking-wide font-outfit uppercase">
                      {project.name}
                    </span>
                  </div>
                  {isActive && (
                    <motion.span
                      layoutId="active-case-study-indicator"
                      className={`absolute inset-0 rounded-full shadow-[0_8px_20px_-6px_rgba(0,0,0,0.1)] dark:shadow-none border -z-10 ${theme.tabGradient}`}
                      transition={{ type: "spring", stiffness: 350, damping: 28 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Project Content Area wrapped inside a premium glassmorphic Card */}
        <Card
          id={`project-tabpanel-${activeProject.index - 1}`}
          role="tabpanel"
          aria-labelledby={`project-tab-${activeProject.index - 1}`}
          variant="default"
          className="w-full bg-white/40 dark:bg-neutral-900/20 backdrop-blur-xl border border-neutral-200/50 dark:border-neutral-800/50 rounded-3xl p-6 sm:p-8 md:p-10 shadow-[0_20px_50px_rgba(0,0,0,0.03)] dark:shadow-none transition-all duration-500 relative overflow-hidden"
        >
          <Card.Content className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center min-h-[500px]">
            
            {/* Details (Left) */}
            <div className="lg:col-span-5 flex flex-col justify-center text-left order-2 lg:order-1">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeProject.index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  className="flex flex-col gap-6"
                >
                  {/* Index / Tagline */}
                  <div className="flex items-center gap-3">
                    <span className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg font-outfit ${theme.accentBg} ${theme.accent}`}>
                      Case Study 0{activeProject.index}
                    </span>
                    <span className="h-1.5 w-1.5 rounded-full bg-neutral-300 dark:bg-neutral-700" />
                    <span className="text-xs font-bold text-neutral-400 dark:text-neutral-300 uppercase tracking-widest font-outfit">
                      {activeProject.tag1.split(" & ")[0]}
                    </span>
                  </div>

                  {/* Project Title */}
                  <h3 className="font-recoleta text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 dark:text-white leading-tight">
                    {activeProject.name}
                  </h3>

                  {/* Project Description */}
                  <p className="font-outfit text-sm sm:text-base text-neutral-500 dark:text-neutral-400 leading-relaxed">
                    {activeProject.description}
                  </p>

                  {/* Features & Highlights */}
                  <div className="space-y-4">
                    <h4 className="font-outfit text-xs font-bold uppercase tracking-wider text-neutral-800 dark:text-neutral-300">
                      Key Project Highlights
                    </h4>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm font-outfit text-neutral-500 dark:text-neutral-400">
                      {activeProject.features?.slice(0, 4).map((feature, fIdx) => (
                        <motion.li
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 + fIdx * 0.05 }}
                          key={fIdx}
                          className="flex items-start gap-2.5"
                        >
                          <svg
                            className="w-4 h-4 mt-0.5 flex-shrink-0"
                            style={{ color: theme.iconColor }}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2.5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span>{feature}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Tech Stack Chips */}
                  <div className="space-y-3">
                    <h4 className="font-outfit text-xs font-bold uppercase tracking-wider text-neutral-800 dark:text-neutral-300">
                      Built With
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {activeProject.tech.slice(0, 6).map((techName, tIdx) => (
                        <Chip
                          key={tIdx}
                          variant="primary"
                          className={`text-[10px] sm:text-xs font-outfit font-medium border rounded-xl px-2.5 py-1 transition-all duration-300 ${theme.chip}`}
                        >
                          {techName}
                        </Chip>
                      ))}
                    </div>
                  </div>

                  {/* Explore Case Study Button */}
                  <div className="pt-4 flex items-center">
                    <Link href={activeProject.href}>
                      <Button
                        size="lg"
                        className={`font-semibold font-outfit rounded-2xl cursor-pointer shadow-lg transition-all duration-300 flex items-center gap-2 px-7 py-4 ${theme.button}`}
                      >
                        <span>Explore Case Study</span>
                        <svg
                          className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                          />
                        </svg>
                      </Button>
                    </Link>
                  </div>

                </motion.div>
              </AnimatePresence>
            </div>

            {/* Dual Mockup Composition (Right) */}
            <div
              ref={containerRef}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              className="lg:col-span-7 flex justify-center items-center w-full order-1 lg:order-2 cursor-pointer"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeProject.index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                  style={{
                    rotateX: reducedMotion ? 0 : rotateX,
                    rotateY: reducedMotion ? 0 : rotateY,
                    transformPerspective: 1200,
                  }}
                  className="relative w-full aspect-[4/3] flex items-center justify-center select-none"
                >
                  
                  {/* Desktop Mockup Card */}
                  <motion.div
                    className="relative w-[85%] lg:w-[88%] aspect-[1.6/1] rounded-2xl bg-neutral-100 dark:bg-neutral-900 border border-neutral-200/50 dark:border-neutral-800/60 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] dark:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.65)] overflow-hidden transition-all duration-500"
                  >
                    {/* Browser Header Bar */}
                    <div className="h-8 border-b border-neutral-200/40 dark:border-neutral-800/60 bg-neutral-200/30 dark:bg-neutral-900/65 flex items-center px-4 gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
                      <div className="mx-auto w-[45%] h-4 rounded-md bg-neutral-200/50 dark:bg-neutral-800/40 border border-neutral-200/10 dark:border-neutral-800/10 flex items-center justify-center">
                        <span className="text-[8px] text-neutral-400 font-outfit truncate">{activeProject.url}</span>
                      </div>
                    </div>

                    {/* Browser Screen Content */}
                    <div className="w-full h-[calc(100%-2rem)] relative bg-neutral-50 dark:bg-neutral-950 overflow-hidden">
                      <Image
                        src={currentScreens.desktopUrl}
                        alt={`${activeProject.name} Desktop Showcase`}
                        width={800}
                        height={500}
                        className="w-full h-auto object-cover object-top"
                        priority
                      />
                    </div>
                  </motion.div>

                  {/* Overlapping Parallax Mobile Mockup Card */}
                  <motion.div
                    style={{
                      x: reducedMotion ? 0 : mobileTranslateX,
                      y: reducedMotion ? 0 : mobileTranslateY,
                    }}
                    whileHover={{
                      scale: reducedMotion ? 1 : 1.05,
                      rotate: reducedMotion ? -3 : -1,
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 22 }}
                    className="absolute bottom-[2%] right-[5%] w-[28%] aspect-[0.52/1] rounded-[2rem] bg-neutral-100 dark:bg-neutral-900 border-[5px] border-neutral-200 dark:border-neutral-800 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.35)] dark:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.75)] overflow-hidden -rotate-3 transition-colors duration-500"
                  >
                    {/* Phone Screen Content */}
                    <div className="w-full h-full relative bg-neutral-50 dark:bg-neutral-950 overflow-hidden">
                      {/* Top Island Notch */}
                      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-[35%] h-3 rounded-full bg-neutral-200 dark:bg-neutral-800 z-20 border border-neutral-300/10 dark:border-neutral-700/10" />
                      
                      <Image
                        src={currentScreens.mobileUrl}
                        alt={`${activeProject.name} Mobile Showcase`}
                        width={260}
                        height={500}
                        className="w-full h-auto object-cover object-top"
                      />
                    </div>
                  </motion.div>

                </motion.div>
              </AnimatePresence>
            </div>

          </Card.Content>
        </Card>

      </div>
    </section>
  );
}
